import React, { useEffect } from 'react'
import ReactModal from 'react-modal'
import { faClose } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Store } from '../../../../../utils/toast'
import { useEditOrderMutation } from '../../../../../store/api/ordersApi'
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers'
import { DigitalClock } from '@mui/x-date-pickers/DigitalClock'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { useDeviceDetect } from '../../../../../utils/use-device-detect'
import './OrdersModal.scss'

import { IOrder } from '../../../../../types/orders'
import { getStorageKey } from '../../../../../utils/appType'

type Props = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  data?: IOrder
}
const c = 'orders-modal'

const EditOrdersModal = (props: Props) => {
  const generalInfo =
    localStorage.getItem(getStorageKey('info')) &&
    JSON.parse(localStorage.getItem(getStorageKey('info')) || '{}')
  const { t } = useTranslation()
  const { isMobile } = useDeviceDetect()
  const [editOrder] = useEditOrderMutation()
  const [date, setDate] = React.useState<Date | null>(new Date())
  const [time, setTime] = React.useState<Date | null>(new Date())

  function submit() {
    const params = {
      date: date?.toLocaleDateString(),
      time: time?.toLocaleTimeString(),
    }

    editOrder({ params, orderId: props.data!._id })
      .unwrap()
      .then(() => {
        Store.addNotification({
          title: 'SUCCESS',
          message: 'Success',
          container: 'top-center',
          type: 'success',
          insert: 'top',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: {
            duration: 1000,
            onScreen: true,
          },
        })
      })
      .catch(() => {
        Store.addNotification({
          title: 'ERROR',
          message: 'Error',
          container: 'top-center',
          type: 'danger',
          insert: 'top',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: {
            duration: 1000,
            onScreen: true,
          },
        })
      })
      .finally(() => {
        props.setIsOpen(false)
      })
  }

  return (
    <ReactModal
      ariaHideApp={false}
      className='orders-modal-container'
      isOpen={props.isOpen}
    >
      <div className={c}>
        <div className={`${c}-close`}>
          <FontAwesomeIcon
            icon={faClose}
            onClick={() => props.setIsOpen(false)}
          />
        </div>
        <div className={`${c}-title`}>Comanda</div>
        <div className={`${c}__content`}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div className={`${c}__content-picker`}>
              <MobileDatePicker
                views={['month', 'day']}
                value={date}
                timezone='Europe/Chisinau'
                onChange={(newValue) => setDate(newValue)}
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    sx: {
                      outline: 'none',
                      '.Mui-focused': {
                        boxShadow: 'var(--color-primary) 0 0 0 2px',
                        background: '#fff',
                        outline: 'var(--color-primary)',
                        borderColor: 'none',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none !important',
                        },
                      },
                      '.MuiOutlinedInput-root:focus-visible': {
                        outline: 'none',
                      },
                      '.MuiOutlinedInput-root:focus': {
                        outline: 'none',
                      },
                    },
                  },
                  day: {
                    sx: {
                      '&.MuiPickersDay-root.Mui-selected': {
                        backgroundColor: 'var(--color-primary)',
                      },
                    },
                  },
                  actionBar: {
                    sx: {
                      '.MuiButton-root': {
                        color: 'var(--color-primary)',
                      },
                    },
                  },
                }}
              />
              <div className={`${c}__content-picker-clock`}>
                <DigitalClock
                  timeStep={15}
                  skipDisabled
                  shouldDisableTime={() => false}
                  timezone='Europe/Chisinau'
                  sx={{
                    '.MuiDigitalClock-item': {
                      transition: 'all 0.2s ease-in-out',
                      '&.Mui-selected': {
                        backgroundColor: 'var(--color-primary)',
                        '&:hover': {
                          backgroundColor: isMobile
                            ? 'none'
                            : 'var(--color-primary-hover)',
                        },
                      },
                    },
                    '.MuiDigitalClock-item:hover': {
                      backgroundColor: isMobile
                        ? 'none'
                        : 'var(--color-primary-hover)',
                      color: isMobile ? '#000000' : '#ffffff',
                    },
                  }}
                  minTime={
                    new Date(
                      0,
                      0,
                      0,
                      Number(generalInfo?.minTime.split(':')[0]),
                    )
                  }
                  maxTime={
                    new Date(
                      0,
                      0,
                      0,
                      Number(generalInfo?.maxTime.split(':')[0]),
                    )
                  }
                  ampm={false}
                  value={time}
                  onChange={(newValue) => {
                    setTime(newValue)
                  }}
                />
              </div>
            </div>
          </LocalizationProvider>
          <div className={`${c}__content-submit`}>
            <Button
              variant='contained'
              type='submit'
              onClick={submit}
              sx={{
                backgroundColor: 'var(--color-primary)',
                textTransform: 'none',
                fontWeight: 'bold',
                width: '100%',
                '&:hover': { backgroundColor: 'var(--color-primary-hover)' },
              }}
            >
              Salveaza
            </Button>
          </div>
        </div>
      </div>
    </ReactModal>
  )
}

export default EditOrdersModal
